import './style.css';
import '../../utils.css';

import type { Dialog, DialogProps } from '@material-ui/core';
import { Dismissible, Divider, Modal, Text, View } from '@zinc/ui';

import { bind } from 'bind-decorator';
import type { IReactionDisposer } from 'mobx';
import { action, autorun, observable } from 'mobx';
import { observer } from 'mobx-react';
import React, { forwardRef, type Ref } from 'react';

import DialogContext from './context';
import type { IAction } from './controls/actions';
import Actions from './controls/actions';

export interface IProps extends Omit<DialogProps, 'classes' | 'open' | 'title'> {
  actions?: (JSX.Element | IAction)[];
  autoClose?: boolean;
  autoDetectWindowHeight?: boolean;
  autoScrollBodyContent?: boolean;
  className?: string;
  header?: JSX.Element;
  open?: boolean;
  openByProps?: boolean;
  repositionOnUpdate?: boolean;
  saving?: boolean;
  style?: React.CSSProperties;
  title: React.ReactNode;
  titleBackground?: string;
  dataTestIdPrefix?: string;
  onCloseButtonClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  onCloseDialog?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  onRequestClose?: () => void;
  dialogWidth?: string;
  dialogWidthSmall?: string;
  staticHeader?: boolean;
  disableCloseOnOutsideClick?: boolean;
  position?: 'center' | 'start' | 'end';
  showCloseIcon?: boolean;
  closeAriaLabel?: string;
}

const DialogWrapperWrapper = forwardRef((props: IProps, ref: Ref) => {
  return <DialogWrapper ref={ref} {...props} />;
});

@observer
class DialogWrapper extends React.Component<IProps> {
  static defaultProps: Partial<IProps> = {
    actions: [],
    autoDetectWindowHeight: true,
    autoScrollBodyContent: true,
    // contentStyle: { width: '100%' },
    open: true,
    openByProps: true,
    repositionOnUpdate: false,

    onCloseDialog: () => null
  };

  static contextType = DialogContext;

  context!: React.ContextType<typeof DialogContext>;

  disposer: IReactionDisposer;

  @observable open = false;

  mobileWindowDisplaySetting = 'inherit';
  dialog: React.RefObject<typeof Dialog> = React.createRef();

  constructor(props) {
    super(props);

    this.disposer = autorun(() => {
      const md = window.mobileDetection;
      if (!(md.tablet() || md.phone())) return;
      const el = document.getElementById('tm2-app');
      this.mobileWindowDisplaySetting = el.style.display;
      const display = this.open ? 'none' : 'inherit';
      el.style.display = display;
    });
    this.open = this.props.open;
  }

  componentDidMount() {
    this.disposer();
  }

  UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (nextProps.autoClose === true) {
      this._close();
    }

    if (nextProps.open !== this.open) {
      this.open = nextProps.open;
    }
  }

  componentWillUnmount() {
    const el = document.getElementById('tm2-app');
    if (el) {
      el.style.display = this.mobileWindowDisplaySetting;
    }
  }

  render() {
    const {
      actions,
      title,
      header,
      children,
      onCloseButtonClick,
      onCloseDialog,
      dialogWidth = '840px',
      dialogWidthSmall = '768px',
      staticHeader = false,
      disableCloseOnOutsideClick = false,
      position = 'end',
      className,
      showCloseIcon = false,
      closeAriaLabel = 'Close modal'
    } = this.props;
    const closeHandler = onCloseButtonClick ?? onCloseDialog;
    const titleContent = (
      <Modal.Title>
        <Text variant="featured-3" weight="bold">
          {title}
        </Text>
        {header}
      </Modal.Title>
    );
    const titleSection = showCloseIcon ? (
      <Dismissible onClose={closeHandler} closeAriaLabel={closeAriaLabel}>
        {titleContent}
      </Dismissible>
    ) : (
      titleContent
    );

    return (
      <View>
        <Modal
          active={this.open}
          onClose={closeHandler}
          position={position}
          size={{ s: dialogWidthSmall, m: dialogWidth }}
          className={className ? `!pt-0 ${className}` : '!pt-0'}
          attributes={{ 'data-testid': 'bz-dialog-wrapper', id: 'bz-dialog-wrapper' }}
          disableCloseOnOutsideClick={disableCloseOnOutsideClick}
        >
          <div ref={this.dialog}>
            <div className={staticHeader ? 'sticky top-0 z-10 bg-white' : ''}>
              <View
                direction="row"
                align="center"
                className="!grid grid-cols-[1fr_auto]"
                padding={2}
                paddingTop={4}
              >
                {titleSection}
                <Actions actions={actions} />
              </View>
              <Divider />
            </div>
            <View paddingTop={2}>{children}</View>
          </div>
        </Modal>
      </View>
    );
  }

  @bind
  onEscape(e: KeyboardEvent): void {
    this._close();
  }

  @bind
  _close(skipCloseCheck: boolean = false) {
    if (!skipCloseCheck && this.open) {
      if (this.props.onRequestClose) {
        return this.props.onRequestClose();
      }
      if (this.props.onCloseButtonClick) {
        this.doClose();
        this.props.onCloseButtonClick();
      }
    }
    // note return statements above... this is a fall-through only...
    this.doClose();
    const { context } = this;
    if (typeof context.onCloseDialog === 'function') {
      this.context.onCloseDialog();
    }
    if (typeof this.props.onCloseDialog === 'function') {
      this.props.onCloseDialog();
    }
  }

  @bind
  onCloseButtonClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (this.props.onRequestClose) {
      this.props.onRequestClose();
    } else if (this.props.onCloseButtonClick) {
      return this.props.onCloseButtonClick(e);
    } else {
      return this._close(!!e);
    }
  }

  @action.bound
  show() {
    this.open = true;
  }

  @bind
  close() {
    this.doClose();
    const { context } = this;
    if (typeof context.onCloseDialog === 'function') {
      this.context?.onCloseDialog?.();
    }
    if (typeof this.props.onCloseDialog === 'function') {
      this.props?.onCloseDialog?.();
    }
  }

  @action.bound
  doClose() {
    this.open = false;
  }

  @bind
  reposition() {
    const { current: dialog } = this.dialog;

    if (!dialog) return;
    return dialog._positionDialog();
  }
}

export default DialogWrapperWrapper;
